import React from 'react';

import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

import {
  IActivityBudgetHeader,
  IAllocationBudgetControl,
  IAllocationSummary,
} from './budgetallocation.models';

type Props={
  summary:IAllocationSummary;
  control:IAllocationBudgetControl;
  header?:IActivityBudgetHeader;
  loading?:boolean;
  canSubmit?:boolean;
  canReopen?: boolean;
  onSubmit?:()=>void;
  onReopen?:()=>void;
};

const money=(value:any)=>
  Number(value||0).toLocaleString(
    'fr-FR',
    {maximumFractionDigits:0},
  );

class BudgetAllocationSummaryPanel
extends React.PureComponent<Props>{

  private metric=(label:string,value:any,caption?:string)=> (
    <Grid size={{xs:6,sm:4,md:2}} key={label}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{fontWeight:700}}>
        {value??0}
      </Typography>
      {caption&&(
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Grid>
  );

  render(){
    const summary=this.props.summary||{};
    const control=this.props.control||{} as IAllocationBudgetControl;
    const header=this.props.header||{} as IActivityBudgetHeader;

    const hasControl=Boolean(control.budgetcontrolid);
    const utilisation=Math.max(
      0,
      Math.min(100,Number(control.utilisationpercent||0)),
    );

    const statecode=String(header.statecode||'').toUpperCase();
    // Submission is a governed workbench capability. Do not infer it only
    // from register readiness: a reopened RETURNED budget is DRAFT and may
    // still be submit-ready financially, but must remain un-submittable until
    // a material correction has been made and the backend enables canSubmit.
    const canSubmit=
      statecode==='DRAFT'
      &&Boolean(this.props.canSubmit)
      &&Boolean(summary.cansubmit)
      &&Boolean(header.stamp);
      
    // REOPEN is valid for an authoritative submission or a returned budget.
    // Once reopened, the header is DRAFT and this action disappears.
    const canReopen =
      Boolean(this.props.canReopen) &&
      ['SUBMITTED', 'RETURNED'].includes(statecode) &&
      Boolean(header.stamp);

    const activityCount=Number(summary.activitycount??summary.activities??0);
    const planned=Number(summary.plannedamount??summary.planned_amount??0);
    const financialised=Number(
      summary.financialisedamount??summary.allocatedamount??summary.allocated_amount??0,
    );
    const variance=Number(summary.varianceamount??0);
    const required=Number(summary.requiredcount??0);
    const ready=Number(summary.requiredreadycount??0);

    return (
      <Stack spacing={2}>
        {!hasControl&&(
          <Alert severity="warning">
            No active Activity Budget Control ceiling is available for this Planning Cycle.
            Allocation may be reviewed, but material allocation must not be completed until Budget Control is active.
          </Alert>
        )}

        {hasControl&&(
          <Card elevation={1}>
            <CardContent>
              <Typography variant="subtitle1" sx={{fontWeight:700,mb:1}}>
                Activity Budget Control
              </Typography>

              <Grid container spacing={2}>
                {this.metric('Activity Ceiling',money(control.ceilingamount),control.currencycode||'XOF')}
                {this.metric('Activity Budget',money(control.allocatedamount),'Authoritative task-budget usage')}
                {this.metric('Available',money(control.availableamount))}
                {this.metric('Utilisation',`${Number(control.utilisationpercent||0).toFixed(2)}%`)}
                {this.metric('Warning At',`${Number(control.warningpercent||0).toFixed(0)}%`)}
                {this.metric('Control Status',control.controlstatus||'UNAVAILABLE')}
              </Grid>

              <LinearProgress
                variant="determinate"
                value={utilisation}
                color={
                  control.controlstatus==='EXCEEDED'
                    ?'error'
                    :control.controlstatus==='WARNING'
                      ?'warning'
                      :'success'
                }
                sx={{mt:2,height:8,borderRadius:4}}
              />
            </CardContent>
          </Card>
        )}

        <Card elevation={1}>
          <CardContent>
            <Stack
              direction={{xs:'column',sm:'row'}}
              justifyContent="space-between"
              alignItems={{xs:'flex-start',sm:'center'}}
              spacing={1}
              sx={{mb:1}}
            >
              <Typography variant="subtitle1" sx={{fontWeight:700}}>
                Activity Budget Summary
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
              {header.statecode&&(
                <Chip
                  size="small"
                  variant="outlined"
                  label={header.statename||header.statecode}
                    color={statecode==='SUBMITTED'?'success':statecode==='RETURNED'?'warning':'default'}
                />
              )}
                {canReopen&&(
                  <Button
                    size="small"
                    variant="contained"
                    color={statecode==='RETURNED'?'warning':'primary'}
                    startIcon={<ReplayOutlinedIcon/>}
                    disabled={this.props.loading}
                    onClick={this.props.onReopen}
                  >
                    Reopen
                  </Button>
                )}

                {canSubmit&&(
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SendOutlinedIcon/>}
                    disabled={this.props.loading}
                    onClick={this.props.onSubmit}
                  >
                    Submit
                  </Button>
                )}
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              {this.metric('Activities',activityCount)}
              {this.metric('Planned',money(planned))}
              {this.metric('Financialised',money(financialised))}
              {this.metric('Net Variance',money(variance))}
              {this.metric('Ready',`${ready} / ${required}`)}
              {this.metric('Variance Cases',Number(summary.requiredvariancecount??0))}
            </Grid>

            <Grid container spacing={2} sx={{mt:0.5}}>
              {this.metric('Budgeted',Number(summary.budgetedcount??0))}
              {this.metric('Variance Up',Number(summary.varianceupcount??0))}
              {this.metric('Variance Down',Number(summary.variancedowncount??0))}
              {this.metric('Pending',Number(summary.pendingcount??0))}
              {this.metric('Partial',Number(summary.partialcount??0))}
              {this.metric('Financially Ready',summary.cansubmit?'YES':'NO')}
            </Grid>
          </CardContent>
        </Card>
      </Stack>
    );
  }
}

export default BudgetAllocationSummaryPanel;
